import { useEffect, useRef } from 'react';
import { Button, notification } from 'antd';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * Listens for a new service-worker version and shows a bottom-right
 * notification asking the user to refresh. Nothing is rendered in the DOM —
 * Ant Design's notification portal handles the UI.
 */
const PWAUpdatePrompt: React.FC = () => {
  const [api, contextHolder] = notification.useNotification();
  const notifiedRef = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      // Poll for updates every 60 seconds while the app is open
      if (registration) {
        setInterval(() => {
          registration.update().catch(() => { /* network may be offline — ignore */ });
        }, 60_000);
        console.debug('[PWA] Service worker registered:', swUrl);
      }
    },
    onRegisterError(error) {
      console.warn('[PWA] Service worker registration failed:', error);
    },
  });

  useEffect(() => {
    if (!needRefresh || notifiedRef.current) return;
    notifiedRef.current = true;

    const key = 'pwa-update';
    api.info({
      key,
      message: 'Update available',
      description: 'A new version of WorkWav is ready. Refresh to get the latest features.',
      placement: 'bottomRight',
      duration: 0, // stays until dismissed or refreshed
      btn: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            notification.destroy(key);
            updateServiceWorker(true); // skip waiting + reload
          }}
        >
          Refresh now
        </Button>
      ),
    });
  }, [needRefresh, api, updateServiceWorker]);

  return <>{contextHolder}</>;
};

export default PWAUpdatePrompt;
